/**
 * Contrato compartilhado entre API e interface.
 * Alterou aqui, os dois lados quebram junto no typecheck — que é o objetivo.
 */

// ---------------------------------------------------------------------------
// Permissões
// ---------------------------------------------------------------------------

/**
 * Catálogo de permissões do WMS.
 * `escrita: true` marca as que só valem com WMS_ALLOW_WRITES ligado.
 */
export const PERMISSOES = {
  'estoque.ver': { grupo: 'Estoque', descricao: 'Consultar estoque e posições', escrita: false },
  'mapa.ver': { grupo: 'Estoque', descricao: 'Visualizar o mapa do CD', escrita: false },
  'historico.ver': {
    grupo: 'Estoque',
    descricao: 'Ver histórico e timeline de posições',
    escrita: false,
  },
  /**
   * Consultar inventários: os escopos de cada um, a etapa e o andamento.
   *
   * Separada de `estoque.ver` porque o andamento de um inventário é o que
   * todo mundo pergunta, mas planejar um é outra conversa — ver
   * `inventario.planejar`.
   */
  'inventario.ver': {
    grupo: 'Estoque',
    descricao: 'Consultar inventários e andamento das contagens',
    escrita: false,
  },
  /**
   * Criar inventário, montar e vincular escopo, congelar estoque.
   *
   * Escrita porque montar e congelar gravam no ERP, e escopo errado vira
   * ajuste de estoque errado lá na integração: um endereço que fica de fora
   * não é contado, e um que entra em dois escopos é ajustado duas vezes.
   */
  'inventario.planejar': {
    grupo: 'Estoque',
    descricao: 'Criar inventário, montar escopos e congelar estoque',
    escrita: true,
  },
  'analytics.ver': {
    grupo: 'Análise',
    descricao: 'Acessar análises e indicadores',
    escrita: false,
  },
  'relatorio.exportar': { grupo: 'Análise', descricao: 'Exportar relatórios', escrita: false },
  'estoque.mover': {
    grupo: 'Operação',
    descricao: 'Movimentar estoque entre posições do mesmo centro',
    escrita: true,
  },
  'transferencia.criar': {
    grupo: 'Operação',
    descricao: 'Criar transferência entre centros',
    escrita: true,
  },
  'transferencia.aprovar': {
    grupo: 'Operação',
    descricao: 'Aprovar transferência entre centros',
    escrita: true,
  },
  /**
   * Emitir onda de separação.
   *
   * Própria, e não um caso de `estoque.mover`: emitir grava no ERP, gera uma
   * etiqueta por unidade e cada etiqueta pendente RESERVA o saldo da posição
   * para as próximas ondas — além de mover os pedidos para o estado 3. É
   * soltar pedido para a rua, não mudar caixa de lugar.
   */
  'separacao.emitir': {
    grupo: 'Operação',
    descricao: 'Emitir onda de separação (gera etiquetas e reserva estoque)',
    escrita: true,
  },
  /**
   * Fechar a conferencia de um pedido.
   *
   * Propria porque e o ato que TRANSFORMA o bipe em verdade: grava a
   * conferencia no ERP com o nome de quem conferiu, e a partir dela o pedido
   * pode ser faturado. Separada de `faturamento.faturar` pelo mesmo motivo que
   * o recebimento separa conferir de aprovar — quem conta a caixa nao e
   * necessariamente quem emite o documento.
   */
  'conferencia.fechar': {
    grupo: 'Operação',
    descricao: 'Fechar conferência de pedido (grava no ERP)',
    escrita: true,
  },
  /**
   * Faturar: gerar a NF-e do pedido conferido.
   *
   * Separada de `separacao.emitir` porque o efeito e a hora sao outros —
   * emitir solta o pedido para a rua, faturar emite documento fiscal com
   * numero, e nao se desfaz por aqui. Quem fatura no chao de fabrica nao
   * necessariamente e quem monta onda.
   */
  'faturamento.faturar': {
    grupo: 'Operação',
    descricao: 'Faturar pedidos conferidos (emite NF-e)',
    escrita: true,
  },
  /**
   * Retirar um pedido de um romaneio já montado.
   *
   * Separada de `faturamento.faturar` porque o gesto é o inverso: faturar
   * emite documento, retirar desfaz a carga de uma viagem que o coletor já
   * fechou. E separada de `estoque.ver` porque, apesar de não mexer em
   * estoque nem em nota, muda o papel que o motorista assina.
   */
  'romaneio.retirar': {
    grupo: 'Operação',
    descricao: 'Retirar pedido cancelado de um romaneio',
    escrita: true,
  },
  /**
   * Aprovar o recebimento depois da conferência cega.
   *
   * Separada de `estoque.ver` porque é o ato que FECHA a conferência: a partir
   * dela a contagem do coletor vira verdade. E separada das demais escritas
   * porque quem confere não deve ser quem aprova — é a mesma razão de o papel
   * ter duas assinaturas.
   */
  'recebimento.aprovar': {
    grupo: 'Operação',
    descricao: 'Aprovar recebimento conferido',
    escrita: true,
  },
  /**
   * Reimprimir etiqueta de alocação.
   *
   * O primeiro print não pede permissão — é parte de receber. A REIMPRESSÃO
   * pede, porque duas etiquetas com o mesmo código circulando pelo CD é como
   * o mesmo volume acaba alocado em dois endereços.
   */
  'recebimento.etiqueta': {
    grupo: 'Operação',
    descricao: 'Reimprimir etiqueta de alocação',
    escrita: true,
  },
  /**
   * Alocar o volume recebido numa posição.
   *
   * É o gesto que MOVE ESTOQUE: dá saída no centro de recebimento e entrada no
   * localizador. Por isso é permissão própria, e não a mesma de imprimir
   * etiqueta — imprimir errado gasta papel, alocar errado cria saldo no lugar
   * errado e só aparece no inventário.
   */
  'recebimento.alocar': {
    grupo: 'Operação',
    descricao: 'Alocar volume recebido em posição',
    escrita: true,
  },
  /**
   * Importar o XML da NF-e do fornecedor e gerar a NF de compra no ERP.
   *
   * Própria porque cria documento fiscal de entrada (NF_COMPRA, parcelas,
   * espelho de custo) pela USP_INS_NF_COMPRA — é trabalho do fiscal/compras,
   * não de quem confere ou aloca a caixa.
   */
  'recebimento.xml': {
    grupo: 'Operação',
    descricao: 'Importar XML de NF de compra (gera a NF de compra no ERP)',
    escrita: true,
  },
  /**
   * Esteira de Entradas (MEG-868): a NF de compra da OC ao estoque. Uma
   * permissão por DONO de etapa — comprador corrige a OC, fiscal dá a entrada,
   * logística agenda, CD recebe — porque a tela mostra a cada um só o botão
   * dele. Liberar acima do DOH e mexer no cadastro ficam fora dos papéis:
   * concessão individual (migration 023).
   */
  'entradas.ver': { grupo: 'Operação', descricao: 'Ver a esteira de Entradas', escrita: false },
  'entradas.comprador': {
    grupo: 'Operação',
    descricao: 'Entradas: indicar/corrigir OC e recusar NF do fornecedor',
    escrita: true,
  },
  'entradas.fiscal': { grupo: 'Operação', descricao: 'Entradas: dar entrada fiscal e tratar exceções', escrita: true },
  'entradas.agendar': { grupo: 'Operação', descricao: 'Entradas: agendar recebimento (data e destino)', escrita: true },
  'entradas.liberar_doh': {
    grupo: 'Operação',
    descricao: 'Entradas: liberar agendamento acima do limite de DOH',
    escrita: true,
  },
  'entradas.receber': {
    grupo: 'Operação',
    descricao: 'Entradas: confirmar chegada (gera a cega) e chegada no Full',
    escrita: true,
  },
  'entradas.cadastro': {
    grupo: 'Administração',
    descricao: 'Entradas: cadastro de destinos, Slack e configuração',
    escrita: true,
  },
  'admin.usuarios': {
    grupo: 'Administração',
    descricao: 'Gerenciar usuários e acessos do WMS',
    escrita: true,
  },
  'admin.total': { grupo: 'Administração', descricao: 'Administrador master', escrita: true },
} as const;

export type Permissao = keyof typeof PERMISSOES;

export const TODAS_PERMISSOES = Object.keys(PERMISSOES) as Permissao[];

// ---------------------------------------------------------------------------
// Sessão e contexto
// ---------------------------------------------------------------------------

export interface UsuarioSessao {
  usuario: number;
  login: string;
  nome: string;
  adminMaster: boolean;
  permissoes: Permissao[];
  /**
   * O cracha do usuario no PBS (`CRACHAS.USUARIO`), quando existe.
   *
   * Conferencia e faturamento no ERP sao fechados POR CRACHA, nao por login:
   * `Usp_FinalizarConferencia @CRACHA` e a emissao de NF-e trabalham assim. O
   * coletor resolve isso pedindo o cracha na abertura do turno; aqui ele vem
   * da sessao, porque o vinculo ja existe no cadastro (165 dos 263 crachas
   * tem USUARIO preenchido, medido em 13/09/2026).
   *
   * `null` = usuario sem cracha vinculado. Nao e caso de borda: e o estado de
   * quem so consulta, e as telas que dependem dele precisam dizer isso em vez
   * de falhar na hora de gravar.
   */
  cracha: string | null;
  /** Impressora de NF-e do cracha, quando cadastrada. */
  impressoraNfe: string | null;
}

/** Contexto multi-tenant selecionado no header. `null` = todos os permitidos. */
export interface ContextoTenant {
  empresa: number | null;
  centroEstoque: number | null;
  tipoEstoque: number | null;
}

export interface OpcoesContexto {
  empresas: { empresa: number; nome: string }[];
  centros: { centroEstoque: number; nome: string; empresa: number; controlaLocalizador: boolean }[];
  tipos: { tipoEstoque: number; nome: string }[];
}

// ---------------------------------------------------------------------------
// Posições e ocupação
// ---------------------------------------------------------------------------

/**
 * Estado visual de uma posição no mapa.
 *
 * ATENÇÃO: `parcial` está declarado mas NÃO é produzido hoje.
 * `CADASTROS_LOCALIZADORES` não tem coluna de capacidade (peso, volume,
 * dimensão, nº de pallets), então não há como calcular ocupação fracionária.
 * Ver docs/blueprint-telas.md, seção "Pendência que afeta o mapa".
 */
export type StatusPosicao =
  'livre' | 'parcial' | 'ocupada' | 'bloqueada' | 'divergencia' | 'indisponivel';

export interface Posicao {
  localizador: number;
  descricao: string;
  empresa: number;
  modulo: string;
  corredor: string;
  box: string;
  nivel: string;
  /** Zona funcional derivada do prefixo do corredor (PP, FR, PCK...). */
  zona: string;
  ativo: boolean;
  status: StatusPosicao;
  /** Nº de SKUs distintos com saldo na posição. */
  skus: number;
  /** Soma dos saldos. Pode ser negativo — a trigger de trava está desativada. */
  saldo: number;

  /**
   * Nível vertical no rack, derivado de BOX (0 = piso, 1..7 = prateleiras).
   * É o eixo Y do desenho do corredor.
   */
  nivelRack: number;
  /** Módulo como número, para ordenação natural. Eixo X do desenho. */
  moduloOrdem: number;
  /** Profundidade dentro do nível (1, 2...). Rack porta-pallet de coluna dupla. */
  posicaoNoNivel: number;
  /**
   * `false` quando o endereço não segue o padrão `N01` conhecido — nesse caso
   * a interface não deve afirmar altura, só listar a posição.
   */
  geometriaConfiavel: boolean;
}

export interface ItemPosicao {
  produto: number;
  descricao: string;
  saldo: number;
  lote: number | null;
  ultimaEntrada: string | null;
  diasNaPosicao: number | null;
}

export interface MapaResposta {
  centroEstoque: number;
  /** Data do último fechamento usado como base. Mostrar como "atualizado até". */
  dataBase: string;
  geradoEm: string;
  posicoes: Posicao[];
  resumo: ResumoOcupacao;
}

export interface ResumoOcupacao {
  posicoesTotal: number;
  posicoesOcupadas: number;
  posicoesLivres: number;
  percentualOcupacao: number;
  skusArmazenados: number;
  posicoesComSaldoNegativo: number;
  /** Saldo em LOCALIZADOR = 0, ou seja, estoque sem endereçamento. */
  estoqueSemEndereco: number;
}

// ---------------------------------------------------------------------------
// Busca
// ---------------------------------------------------------------------------

export type TipoBusca = 'sku' | 'endereco' | 'descricao';

export interface ResultadoBusca {
  tipoDetectado: TipoBusca;
  termo: string;
  itens: LinhaEstoque[];
  total: number;
}

export interface LinhaEstoque {
  produto: number;
  produtoDescricao: string;
  empresa: number;
  centroEstoque: number;
  localizador: number;
  endereco: string;
  corredor: string;
  zona: string;
  saldo: number;
}

// ---------------------------------------------------------------------------
// Timeline
// ---------------------------------------------------------------------------

export interface EventoTimeline {
  dataHora: string;
  produto: number;
  produtoDescricao: string;
  entrada: number;
  saida: number;
  saldoApos: number;
  tipoMovimento: number;
  tipoMovimentoDescricao: string;
  documento: number | null;
}

// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------

export interface Dashboard {
  dataBase: string;
  resumo: ResumoOcupacao;
  movimentacoesHoje: { entradas: number; saidas: number; transferencias: number };
  ocupacaoPorZona: { zona: string; total: number; ocupadas: number; percentual: number }[];
  serieOcupacao: { data: string; ocupadas: number; total: number; percentual: number }[];
  alertas: Alerta[];
}

export interface Alerta {
  tipo: 'consolidacao' | 'saldo_negativo' | 'parado' | 'capacidade' | 'sem_endereco';
  severidade: 'info' | 'atencao' | 'critico';
  titulo: string;
  detalhe: string;
  quantidade: number;
  /** Link para a tela que mostra a origem do número. Todo alerta é investigável. */
  href: string;
}

// ---------------------------------------------------------------------------
// Erros
// ---------------------------------------------------------------------------

export interface ErroApi {
  erro: string;
  mensagem: string;
  detalhes?: unknown;
}

// ---------------------------------------------------------------------------
// Produto
// ---------------------------------------------------------------------------

export interface Produto {
  sku: number;
  descricao: string;
  /** Nome comercial de `CADASTROS_PRODUTO_SKUS`. Pode divergir de `descricao`. */
  nome: string | null;
  ean: string | null;
  partNumber: string | null;
  ncm: string | null;
  pesoBrutoKg: number | null;
  alturaCm: number | null;
  larguraCm: number | null;
  profundidadeCm: number | null;
  /** Calculado das dimensões. A coluna VOLUME do PBS não serve — vem sempre 1. */
  volumeM3: number | null;
  familia: string | null;
  secao: string | null;
  grupo: string | null;
  marca: string | null;
  /**
   * `false` quando o SKU não tem linha em `CADASTROS_PRODUTO_SKUS` — só temos
   * a descrição. Acontece de verdade (ex.: SKU 11314), e a interface deve
   * mostrar o que tem em vez de campos vazios sem explicação.
   */
  temAtributos: boolean;
  imagens: string[];
}

/**
 * URL da imagem de um SKU no servidor de arquivos da Megamamute.
 *
 * Padrão verificado em 2026-09-11:
 *   https://arquivo.megamamute.com.br/produtos/imagens/{2 últimos dígitos}/{sku}/{sku}_original{n}.jpg
 *
 * O `n` é vazio para a principal e 1..N para as demais.
 * Ex.: SKU 11314 -> .../imagens/14/11314/11314_original.jpg
 *
 * **Nem todo SKU tem imagem** (32991 e 33112 devolvem 404). A interface deve
 * tratar erro de carregamento como ausência, não como falha.
 */
export const BASE_IMAGENS = 'https://arquivo.megamamute.com.br/produtos/imagens';

export function urlImagemProduto(sku: number, indice = 0): string {
  const id = String(sku);
  // A pasta são os dois últimos dígitos do SKU. SKUs de 1 dígito usam o próprio.
  const pasta = id.length >= 2 ? id.slice(-2) : id;
  const sufixo = indice === 0 ? '' : String(indice);
  return `${BASE_IMAGENS}/${pasta}/${id}/${id}_original${sufixo}.jpg`;
}

/**
 * Candidatas de imagem para uma galeria.
 *
 * Não há endpoint que liste as imagens existentes, então a interface pede as
 * primeiras N e esconde as que falharem ao carregar. `quantidade` maior gera
 * mais 404 no console — 5 cobre os casos observados (o SKU 11314 tem 5).
 */
export function urlsImagemProduto(sku: number, quantidade = 5): string[] {
  return Array.from({ length: quantidade }, (_, i) => urlImagemProduto(sku, i));
}

export * from './cron.js';
export * from './divergencias.js';
